import React from 'react';
// Modules
import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/node';
import { parsePhoneNumber } from 'libphonenumber-js';
import Router from 'next/router';
import * as yup from 'yup';
// Firebase
import firebase from '../firebase/client';
// Utils
import { DEFAULT_ERROR_MESSAGE } from '../utils/constants';
import { parseError } from '../utils/helpers';
import routes from '../utils/routes';

const emailValidator = yup.string().email().required();

function useAuthForm() {
  const toast = useToast();

  const [code, setCode] = React.useState('');
  const [
    confirmationResult,
    setConfirmationResult,
  ] = React.useState<firebase.auth.ConfirmationResult>();
  const [displayPasswordInput, setDisplayPasswordInput] = React.useState(false);
  const [isEmailValid, setEmailValid] = React.useState(false);
  const [isLoading, setLoading] = React.useState(false);
  const [isPhoneValid, setPhoneValid] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [username, setUsername] = React.useState('');

  React.useEffect(() => {
    emailValidator
      .isValid(username)
      .then((value) => {
        setEmailValid(value);
      })
      .catch(() => {
        setEmailValid(false);
      });

    try {
      const phoneNumber = parsePhoneNumber(username, 'ID');
      setPhoneValid(phoneNumber.isValid());
    } catch (e) {
      setPhoneValid(false);
    }
  }, [username]);

  async function onClickContinue() {
    if (isEmailValid) {
      setDisplayPasswordInput(true);

      return;
    }

    if (isPhoneValid) {
      const recaptchaVerifier = new firebase.auth.RecaptchaVerifier('btn-continue', {
        size: 'invisible',
      });

      try {
        setLoading(true);

        const phoneNumber = parsePhoneNumber(username, 'ID');

        const confirmation = await firebase
          .auth()
          .signInWithPhoneNumber(phoneNumber.number.toString(), recaptchaVerifier);

        setConfirmationResult(confirmation);

        setLoading(false);
      } catch (e) {
        toast({
          description: parseError(e),
          status: 'error',
        });

        setLoading(false);

        Sentry.captureException(e);
      }

      recaptchaVerifier.clear();
    }
  }

  async function onClickLogIn() {
    if (isEmailValid) {
      try {
        setLoading(true);

        await firebase.auth().signInWithEmailAndPassword(username, password);

        await Router.push(routes.home.href);
      } catch (e) {
        toast({
          description: parseError(e),
          status: 'error',
        });

        setLoading(false);

        Sentry.captureException(e);
      }
    }

    if (isPhoneValid) {
      await onSubmitPhoneAuth();
    }
  }

  async function onClickFacebook() {
    try {
      const provider = new firebase.auth.FacebookAuthProvider();
      provider.addScope('email');

      await firebase.auth().signInWithPopup(provider);

      await Router.push(routes.home.href);
    } catch (e) {
      if (e.code !== 'auth/popup-closed-by-user') {
        toast({
          description: parseError(e),
          status: 'error',
        });

        Sentry.captureException(e);
      }
    }
  }

  async function onClickGoogle() {
    try {
      const provider = new firebase.auth.GoogleAuthProvider();

      await firebase.auth().signInWithPopup(provider);

      await Router.push(routes.home.href);
    } catch (e) {
      if (e.code !== 'auth/popup-closed-by-user') {
        toast({
          description: parseError(e),
          status: 'error',
        });

        Sentry.captureException(e);
      }
    }
  }

  async function onClickSignUp() {
    if (isEmailValid) {
      try {
        setLoading(true);

        await firebase.auth().createUserWithEmailAndPassword(username, password);

        await Router.push(routes.home.href);
      } catch (e) {
        toast({
          description: parseError(e),
          status: 'error',
        });

        setLoading(false);

        Sentry.captureException(e);
      }
    }

    if (isPhoneValid) {
      await onSubmitPhoneAuth();
    }
  }

  async function onSubmitPhoneAuth() {
    try {
      setLoading(true);

      if (!confirmationResult) {
        throw new Error(DEFAULT_ERROR_MESSAGE);
      }

      await confirmationResult.confirm(code);

      await Router.push(routes.home.href);
    } catch (e) {
      toast({
        description: parseError(e),
        status: 'error',
      });

      setLoading(false);

      Sentry.captureException(e);
    }
  }

  return {
    code,
    confirmationResult,
    displayPasswordInput,
    isEmailValid,
    isLoading,
    isPhoneValid,
    onClickContinue,
    onClickFacebook,
    onClickGoogle,
    onClickLogIn,
    onClickSignUp,
    password,
    setCode,
    setPassword,
    setUsername,
    username,
  };
}

export default useAuthForm;
